import type { ModelComparisonCompareTextBlock } from '@backmarket/http-api/src/api-specs-model-comparison/models/compare-models'

import {
  PRODUCT_COMPARISON_CAMERA_FIELDS,
  PRODUCT_COMPARISON_DURABILITY_FIELDS,
  PRODUCT_COMPARISON_MISCELANEOUS_FIELDS,
  PRODUCT_COMPARISON_MULTIMEDIA_FIELDS,
  PRODUCT_COMPARISON_PERFORMANCE_FIELDS,
  PRODUCT_COMPARISON_SCREEN_QUALITY_FIELDS,
  PRODUCT_COMPARISON_SOUND_QUALITY_FIELDS,
} from '../composables/useEnrichResultsPage/useEnrichResultsPage.constants'
import type { Line, TableData } from '../models/productComparison'

export type Scores = Record<string, string | undefined>

export type ProductSpecification = {
  field: string
  display: string
  values: Array<{
    label: string
  }>
}

export function getSpecificationField(
  productsSpecifications: ProductSpecification[][],
  field: string,
): Line {
  let title = ''
  const values = productsSpecifications.map((productsSpecification) => {
    const productSpecification = productsSpecification.find(
      (i) => i.field === field,
    )
    if (!productSpecification) return null
    if (productSpecification.display) title = productSpecification.display

    return productSpecification.values.map((i) => i.label).join(', ')
  })

  return {
    type: 'text',
    title,
    values,
  }
}

export function getSpecificationFields(
  productsSpecification: ProductSpecification[][],
  fields: string[],
): Line[] {
  return fields.map((field) =>
    getSpecificationField(productsSpecification, field),
  )
}

export function getScoreField(
  scores: Scores[],
  scoreField: keyof Scores,
): Line {
  return {
    type: 'scores',
    values: scores.map((score) => parseFloat(score?.[scoreField] ?? '0')),
  }
}

export function getTextBlockField(
  textBlocks: ModelComparisonCompareTextBlock[],
  id: string,
) {
  const textBlock = textBlocks.find((tb) => tb.section === id)
  if (!textBlock) return undefined

  return {
    format: textBlock?.format,
    value: textBlock?.value,
  }
}

export type ComparisonTableLabels = {
  ratings: string
  scores: string
  durability: string
  performance: string
  screenQuality: string
  soundQuality: string
  multimedia: string
  camera: string
  miscelaneous: string
}

export function buildComparisonTable({
  textBlocks,
  specs,
  scores,
  ratings,
  labels,
}: {
  textBlocks: ModelComparisonCompareTextBlock[]
  specs: ProductSpecification[][]
  scores: Scores[]
  ratings: number[]
  labels: ComparisonTableLabels
}): TableData[] {
  function scoreSection(
    title: string,
    trackingKey: string,
    scoreId: string,
    fields: string[],
  ) {
    return {
      title,
      trackingKey,
      textBlock: getTextBlockField(textBlocks, scoreId),
      lines: [
        getScoreField(scores, scoreId),
        ...getSpecificationFields(specs, fields),
      ],
    }
  }

  const table: TableData[] = [
    {
      sections: [
        {
          textBlock: getTextBlockField(textBlocks, 'introduction'),
          lines: [
            {
              type: 'ratings',
              title: labels.ratings,
              values: ratings,
            },
          ],
        },
      ],
    },
    {
      title: labels.scores,
      sections: [
        scoreSection(
          labels.durability,
          'comparison_tool_durability',
          'life_expectancy_score',
          PRODUCT_COMPARISON_DURABILITY_FIELDS,
        ),
        scoreSection(
          labels.performance,
          'comparison_tool_performance',
          'performances_score',
          PRODUCT_COMPARISON_PERFORMANCE_FIELDS,
        ),
        scoreSection(
          labels.screenQuality,
          'comparison_tool_screen_quality',
          'screen_quality_score',
          PRODUCT_COMPARISON_SCREEN_QUALITY_FIELDS,
        ),
        scoreSection(
          labels.soundQuality,
          'comparison_tool_sound_quality',
          'sound_quality_score',
          PRODUCT_COMPARISON_SOUND_QUALITY_FIELDS,
        ),
        scoreSection(
          labels.multimedia,
          'comparison_tool_multimedia',
          'multimedia_score',
          PRODUCT_COMPARISON_MULTIMEDIA_FIELDS,
        ),
        scoreSection(
          labels.camera,
          'comparison_tool_camera',
          'camera_score',
          PRODUCT_COMPARISON_CAMERA_FIELDS,
        ),
      ],
    },
    {
      title: labels.miscelaneous,
      separator: true,
      sections: [
        {
          trackingKey: 'comparison_tool_miscelaneous',
          textBlock: getTextBlockField(textBlocks, 'miscellaneous'),
          lines: getSpecificationFields(
            specs,
            PRODUCT_COMPARISON_MISCELANEOUS_FIELDS,
          ),
        },
        {
          textBlock: getTextBlockField(textBlocks, 'conclusion'),
          lines: [],
        },
      ],
    },
  ]

  return cleanTableData(table)
}

export function cleanTableData(table: TableData[]) {
  for (const tableGroup of table) {
    for (const section of tableGroup.sections) {
      // Filter empty lines
      section.lines = section.lines.filter((line) => {
        if (line.type === 'colors' || line.type === 'tags')
          return line.values?.every((v) => v?.length > 0)
        if (line.type === 'ratings' || line.type === 'scores')
          return line.values?.every((v) => v > 0)
        if (line.type === 'info') return !!line.value

        return line.values?.every((v) => !!v)
      })
    }
    // Filter empty sections
    tableGroup.sections = tableGroup.sections.filter(
      (section) => !!section.lines?.length || !!section.textBlock,
    )
  }

  // Filter empty tableGroups
  return table.filter((tableGroup) => !!tableGroup.sections.length)
}
